import React, { useRef, useEffect, useState, ReactElement, FocusEvent, MouseEvent } from 'react';
import Input from '../../classes/Input'
import { ClearIcon } from '../Iconset';
import { CustomEventTypes } from '../../globals';
import useEventListener from '../reactUtils/useEventListener';
import { getUuidv4 } from '../../classes/utilities/Misc';
import AnalyticsManager from '../../classes/AnalyticsManager';
import { KeyCodes } from '../../interfaces/Input';

interface ISearchFieldProps
{
	value: string;
	id?: string;
	category?: string;
	autoFocus?: boolean;
	readOnly?: boolean;
	placeholder?: string;
	className?: string;
	fieldClassName?: string;
	prefixIcon?: ReactElement;
	'data-name'?: string;
	onChangeCallback: (value: string) => void;
	onCommitCallback?: () => void;
	onFocusCallback?: (e: FocusEvent<HTMLInputElement>) => void;
	onBlurCallback?: (e: FocusEvent<HTMLInputElement>) => void;
	onClick?: (e: MouseEvent<HTMLInputElement>) => void;
}

export default function SearchField(props: ISearchFieldProps)
{
	const readonly = props.readOnly !== undefined ? props.readOnly : false;
	const inputRef = useRef<HTMLInputElement>(null);
	const [id, setId] = useState(props.id || getUuidv4());

	const [showClearButton, setShowClearButton] = useState(false);

	useEffect(() =>
	{
		setShowClearButton((props.value || inputRef.current?.value) ? true : false);
	});

	useEffect(() => 
	{
		if(props.id) setId(props.id);
	}, [props.id]);

	useEventListener(CustomEventTypes.onInputUpdated, () =>
	{
		if(!inputRef.current || inputRef.current !== document.activeElement) return;

		if(Input.getKeyDown(KeyCodes.ESCAPE))
		{
			if (props.onChangeCallback)
			{
				props.onChangeCallback("");
			}
		}
		else if (Input.getKeyDown(KeyCodes.ENTER))
		{
			inputRef.current.blur();
		}
	}, document);

	useEffect(() =>
	{
		if (inputRef.current && props.autoFocus)
		{
			inputRef.current.focus();
		}
	}, [props.autoFocus]);

	return (
		<div className={["searchFieldContainer", props.className].join(' ')}
			data-name={"search-field-root"}
		>
			{ 
				props.prefixIcon && React.cloneElement(props.prefixIcon, { className: "searchFieldPrefixIcon" })
			}
			<input className={["textField", props.fieldClassName, (props.prefixIcon ? "withPrefixIcon" : "")].join(' ')}
				ref={inputRef}
				id={id}
				data-name={props['data-name'] ?? "search-field-input"}
				placeholder={props.placeholder ? props.placeholder : "Search"}
				value={props.value}
				readOnly={readonly}
				onChange={(e) =>
				{
					if (props.onChangeCallback)
						props.onChangeCallback(e.target.value);
				}}
				onFocus={(e) => 
				{
					props.onFocusCallback?.(e);
				}}
				onBlur={(e) =>
				{
					AnalyticsManager.managedEvent('searchEvent', 'managedEventData', e.target.dataset.name, props.category ?? "Search Field")
					if (!readonly && props.onCommitCallback)
					{
						props.onCommitCallback();
					}

					props.onBlurCallback?.(e);
				}}
				onClick={(e) => 
				{
					e.stopPropagation();
					props.onClick?.(e);
				}}
			/>
			{
				!showClearButton ? null :
					<ClearIcon className={"searchFieldClearButton"}
						scale={0.7}
						onClick={() =>
						{
							if (props.onChangeCallback)
							{
								props.onChangeCallback("");
							}
						}}
					/>
			}
		</div>
	);
}